/**
 * Font Loader Utility
 * 
 * This utility implements a hybrid font loading strategy using FontFace Observer.
 * It attempts to load fonts from Google Fonts CDN first, and if that fails,
 * falls back to self-hosted versions of the same fonts.
 * 
 * Key features:
 * - Optimized font loading from Google Fonts
 * - Robust error handling and fallback to self-hosted fonts
 * - Performance tracking and debugging capabilities
 * - Cross-browser compatible
 */

import FontFaceObserver from 'fontfaceobserver';

// Font configuration
interface FontConfig {
  family: string;
  weights: number[];
  styles: string[];
}

// Configuration for the fonts we need
const FONTS: FontConfig[] = [
  { 
    family: 'Literata', 
    weights: [400, 700],
    styles: ['normal', 'italic']
  },
  {
    family: 'Rubik',
    weights: [400, 500, 700],
    styles: ['normal', 'italic']
  }
];

// Font loading timeout (ms)
const FONT_TIMEOUT = 2000;

// Debug mode - set to true to enable detailed logging
let DEBUG_MODE = false;

// Track which source is active
let _activeSource: 'google' | 'self-hosted' | null = null;

// Track loading times for performance monitoring
const _timings = {
  start: 0,
  googleStart: 0,
  googleEnd: 0,
  selfHostedStart: 0,
  selfHostedEnd: 0,
  end: 0
};

/**
 * Enable or disable debug mode
 */
export function setDebugMode(enable: boolean): void {
  DEBUG_MODE = enable;
}

/**
 * Set the active font source - used internally and by font-manager
 */
export function setActiveSource(source: 'google' | 'self-hosted' | null): void {
  _activeSource = source;
}

/**
 * Get the active font source
 */
export function getActiveFontSource(): 'google' | 'self-hosted' | null {
  return _activeSource;
}

/**
 * Get performance timings for font loading
 */
export function getFontLoadingTimings() {
  return { ..._timings };
}

/**
 * Debug logging helper
 */
function log(message: string, type: 'info' | 'warn' | 'error' = 'info'): void {
  if (!DEBUG_MODE) return;
  
  const timestamp = _timings.start > 0 ? 
    `[${(performance.now() - _timings.start).toFixed(1)}ms]` : 
    '[init]';
    
  switch (type) {
  case 'warn':
    console.warn(`${timestamp} Font Loader: ${message}`);
    break;
  case 'error':
    console.error(`${timestamp} Font Loader: ${message}`);
    break;
  default:
    console.log(`${timestamp} Font Loader: ${message}`);
  }
}

/**
 * Add preconnect links for Google Fonts to improve loading performance
 */
function addGoogleFontsPreconnect(): void {
  if (typeof document === 'undefined') return;
  
  // Check if preconnect links already exist
  const existingPreconnect = document.querySelector('link[rel="preconnect"][href="https://fonts.googleapis.com"]');
  const existingCrossOrigin = document.querySelector('link[rel="preconnect"][href="https://fonts.gstatic.com"][crossorigin]');
  
  if (existingPreconnect && existingCrossOrigin) {
    log('Preconnect for Google Fonts domains already exists');
    return;
  }
  
  log('Adding preconnect for Google Fonts domains');
  
  // Preconnect to fonts.googleapis.com (where CSS is hosted)
  if (!existingPreconnect) {
    const preconnectGoogleapis = document.createElement('link');
    preconnectGoogleapis.rel = 'preconnect';
    preconnectGoogleapis.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnectGoogleapis);
  }
  
  // Preconnect to fonts.gstatic.com (where font files are hosted)
  if (!existingCrossOrigin) {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = 'https://fonts.gstatic.com';
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
}

/**
 * Create font observers for all configured font variants
 */
function createFontObservers(): FontFaceObserver[] {
  const observers: FontFaceObserver[] = [];
  
  FONTS.forEach(font => {
    font.weights.forEach(weight => {
      font.styles.forEach(style => {
        log(`Creating observer for ${font.family} ${weight} ${style}`);
        const observer = new FontFaceObserver(font.family, {
          weight: weight,
          style: style === 'italic' ? 'italic' : 'normal'
        });
        observers.push(observer);
      });
    });
  });
  
  return observers;
}

/**
 * Add Google Fonts stylesheet to the document
 */
function addGoogleFontsStylesheet(): void {
  if (typeof document === 'undefined') return;
  
  const existingLink = document.querySelector('link[data-source="google-fonts"]');
  if (existingLink) {
    log('Google Fonts stylesheet already exists');
    return;
  }
  
  const googleFontsURL = 'https://fonts.googleapis.com/css2?family=Literata:ital,wght@0,400;0,700;1,400;1,700&family=Rubik:wght@400;500;700&display=swap';
  
  log(`Adding Google Fonts stylesheet: ${googleFontsURL}`);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = googleFontsURL;
  link.setAttribute('data-source', 'google-fonts');
  document.head.appendChild(link);
}

/**
 * Generate @font-face CSS for self-hosted fonts
 */
export function generateSelfHostedFontFaceCss(): string {
  const fontFaceCss: string[] = [];
  
  FONTS.forEach(font => {
    font.weights.forEach(weight => {
      font.styles.forEach(style => {
        const isItalic = style === 'italic';
        fontFaceCss.push(`
/* ${font.family} ${weight} ${style} */
@font-face {
  font-family: '${font.family}';
  font-style: ${isItalic ? 'italic' : 'normal'};
  font-weight: ${weight};
  src: url('/fonts/${font.family.toLowerCase()}/${isItalic ? (weight === 700 ? 'bold-italic' : 'italic') : (weight === 700 ? 'bold' : (weight === 500 ? 'medium' : 'regular'))}.woff2') format('woff2'),
       url('/fonts/${font.family.toLowerCase()}/${isItalic ? (weight === 700 ? 'bold-italic' : 'italic') : (weight === 700 ? 'bold' : (weight === 500 ? 'medium' : 'regular'))}.ttf') format('truetype');
  font-display: swap;
}`);
      });
    });
  });
  
  return fontFaceCss.join('\n');
}

/**
 * Add self-hosted font definitions
 */
export function injectSelfHostedFontDefinitions(): void {
  if (typeof document === 'undefined') return;
  
  if (document.querySelector('style[data-source="self-hosted-fonts"]')) {
    log('Self-hosted font definitions already exist');
    return;
  }
  
  log('Injecting self-hosted font definitions');
  
  const style = document.createElement('style');
  style.setAttribute('data-source', 'self-hosted-fonts');
  style.textContent = generateSelfHostedFontFaceCss();
  document.head.appendChild(style);
  
  log('Self-hosted font definitions injected');
}

/**
 * Try to load fonts using FontFaceObserver
 */
function tryLoadFonts(observers: FontFaceObserver[]): Promise<unknown> {
  return Promise.all(
    observers.map(observer => 
      observer.load(null, FONT_TIMEOUT)
        .then(() => {
          log(`Font loaded: ${observer.family} ${observer.weight || 'normal'} ${observer.style || 'normal'}`);
        })
        .catch(error => {
          log(`Failed to load font: ${observer.family} ${observer.weight || 'normal'} ${observer.style || 'normal'}: ${error.message}`, 'error');
          throw error;
        })
    )
  );
}

/**
 * Load Google Fonts
 */
function loadGoogleFonts(): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  
  _timings.googleStart = performance.now();
  log('Starting Google Fonts loading');
  
  // Setup for Google Fonts
  addGoogleFontsPreconnect();
  addGoogleFontsStylesheet();
  
  // Create observers
  const observers = createFontObservers();
  
  // Try to load the fonts
  return tryLoadFonts(observers)
    .then(() => {
      _timings.googleEnd = performance.now();
      log(`Google Fonts loaded in ${(_timings.googleEnd - _timings.googleStart).toFixed(1)}ms`);
      return true;
    })
    .catch(error => {
      _timings.googleEnd = performance.now();
      log(`Google Fonts failed after ${(_timings.googleEnd - _timings.googleStart).toFixed(1)}ms: ${error.message}`, 'error');
      
      // Clean up Google Fonts elements
      const googleFontsElements = document.querySelectorAll('[data-source="google-fonts"]');
      googleFontsElements.forEach(element => {
        log(`Removing Google Fonts element: ${element.tagName}`);
        element.remove();
      });
      
      throw error;
    });
}

/**
 * Load self-hosted fonts
 */
function loadSelfHostedFonts(): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  
  _timings.selfHostedStart = performance.now();
  log('Starting self-hosted fonts loading');
  
  // Inject self-hosted font definitions
  injectSelfHostedFontDefinitions();
  
  // Create observers
  const observers = createFontObservers();
  
  // Try to load the fonts
  return tryLoadFonts(observers)
    .then(() => {
      _timings.selfHostedEnd = performance.now();
      log(`Self-hosted fonts loaded in ${(_timings.selfHostedEnd - _timings.selfHostedStart).toFixed(1)}ms`);
      return true;
    })
    .catch(error => {
      _timings.selfHostedEnd = performance.now();
      log(`Self-hosted fonts failed after ${(_timings.selfHostedEnd - _timings.selfHostedStart).toFixed(1)}ms: ${error.message}`, 'error');
      throw error;
    });
}

/**
 * Main function to load fonts
 * Tries Google Fonts first, then falls back to self-hosted fonts
 */
export function loadFonts(forceSource?: 'google' | 'self-hosted'): Promise<boolean> {
  if (typeof document === 'undefined') return Promise.resolve(false);
  
  // Reset state
  _activeSource = null;
  _timings.start = performance.now();
  
  // Update CSS classes
  document.documentElement.classList.add('fonts-loading');
  document.documentElement.classList.remove('fonts-loaded');
  document.documentElement.classList.remove('fonts-failed');
  document.documentElement.classList.remove('fonts-google');
  document.documentElement.classList.remove('fonts-self-hosted');
  
  log('Starting font loading' + (forceSource ? ` (forced source: ${forceSource})` : ''));
  
  // Function to finalize the font loading process
  const finalize = (success: boolean): boolean => {
    _timings.end = performance.now();
    const duration = _timings.end - _timings.start;
    
    // Update CSS classes
    document.documentElement.classList.remove('fonts-loading');
    
    if (success) {
      document.documentElement.classList.add('fonts-loaded');
      log(`Fonts loaded successfully in ${duration.toFixed(1)}ms from ${_activeSource}`);
      
      // Save successful source to localStorage
      if (_activeSource) {
        try {
          const data = {
            timestamp: Date.now(),
            source: _activeSource
          };
          localStorage.setItem('fontLoadData', JSON.stringify(data));
          log(`Saved font source to localStorage: ${_activeSource}`);
        } catch (e) {
          log(`Error saving to localStorage: ${e instanceof Error ? e.message : String(e)}`, 'error');
        }
      }
      
      // Dispatch success event
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        const event = new CustomEvent('fontsLoaded', { 
          detail: { 
            source: _activeSource,
            duration
          }
        });
        window.dispatchEvent(event);
      }
    } else {
      document.documentElement.classList.add('fonts-failed');
      // Ensure we remove any source-specific classes when fonts fail
      document.documentElement.classList.remove('fonts-google');
      document.documentElement.classList.remove('fonts-self-hosted');
      log(`Font loading failed after ${duration.toFixed(1)}ms`, 'error');
      
      // Dispatch failure event
      if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
        const event = new CustomEvent('fontsFailed', { 
          detail: { duration }
        });
        window.dispatchEvent(event);
      }
    }
    
    return success;
  };
  
  // Handle forced source
  if (forceSource === 'google') {
    return loadGoogleFonts()
      .then(() => {
        _activeSource = 'google';
        document.documentElement.classList.add('fonts-google');
        return finalize(true);
      })
      .catch(() => finalize(false));
  } 
  else if (forceSource === 'self-hosted') {
    return loadSelfHostedFonts()
      .then(() => {
        _activeSource = 'self-hosted';
        document.documentElement.classList.add('fonts-self-hosted');
        return finalize(true);
      })
      .catch(() => finalize(false));
  }
  
  // Try Google Fonts first, then fall back to self-hosted
  return loadGoogleFonts()
    .then(() => {
      _activeSource = 'google';
      document.documentElement.classList.add('fonts-google');
      return finalize(true);
    })
    .catch(error => {
      log(`Google Fonts failed: ${error.message}. Falling back to self-hosted fonts.`, 'warn');
      
      // Fall back to self-hosted fonts
      return loadSelfHostedFonts()
        .then(() => {
          _activeSource = 'self-hosted';
          document.documentElement.classList.add('fonts-self-hosted');
          return finalize(true);
        })
        .catch(() => finalize(false));
    });
}

// Create a global interface for non-module scripts
interface FontLoader {
  loadFonts: typeof loadFonts;
  setDebugMode: typeof setDebugMode;
  getActiveFontSource: typeof getActiveFontSource;
  getFontLoadingTimings: typeof getFontLoadingTimings;
  setActiveSource: typeof setActiveSource;
  injectSelfHostedFontDefinitions: typeof injectSelfHostedFontDefinitions;
}

// Expose the font loader to window
if (typeof window !== 'undefined') {
  (window as any).fontLoader = {
    loadFonts,
    setDebugMode,
    getActiveFontSource,
    getFontLoadingTimings,
    setActiveSource,
    injectSelfHostedFontDefinitions
  } as FontLoader;
}